/**
 * Route definitions with lazy-loaded page modules.
 * Add new pages here — the router handles the rest.
 */

import type { RouteConfig } from "./router.js";

export const routes: RouteConfig[] = [
  {
    path: "/workflows",
    title: "Workflows",
    icon: "workflow",
    section: "primary",
    subsections: [
      { id: "wf-new-btn", label: "New", click: "#wf-new-btn" },
      { id: "wf-library", label: "Library" },
    ],
    load: () => import("../../DOM/pages/workflow/index.js").then(m => m.page),
  },
  {
    path: "/streams",
    title: "Streams",
    icon: "video",
    section: "primary",
    subsections: [
      { id: "cmd-grid", label: "Live Sessions" },
      { id: "recordings-section", label: "Recordings" },
    ],
    load: () => import("../../DOM/pages/live-gallery.js").then(m => m.page),
  },
  {
    path: "/room",
    title: "Room",
    icon: "spatial",
    /** Hidden from sidebar — accessed via "Room" button on stream cards */
    section: undefined,
    load: () => import("../../DOM/pages/room/index.js").then(m => m.page),
  },
  {
    path: "/apps",
    title: "Apps",
    icon: "apps",
    section: "primary",
    subsections: [
      { id: "apps-create-link", label: "New", href: "#/apps/new" },
      { id: "apps-grid", label: "Library" },
    ],
    load: () => import("../../DOM/pages/apps/index.js").then(m => m.page),
  },
  {
    path: "/publish",
    title: "Publish",
    icon: "broadcast",
    section: "primary",
    subsections: [
      { id: "pub-device-id", label: "Device" },
      { id: "pub-controls", label: "Stream" },
    ],
    load: () => import("../../DOM/pages/publisher/index.js").then(m => m.page),
  },
  {
    path: "/account",
    title: "Account",
    icon: "account",
    section: "admin",
    subsections: [
      { id: "devices-stats", label: "Device Fleet" },
      { id: "analytics-kpis", label: "Analytics" },
    ],
    load: () => import("../../DOM/pages/account.js").then(m => m.page),
  },
  {
    path: "/help",
    title: "Help",
    icon: "help",
    section: "admin",
    load: () => import("../../DOM/pages/help").then(m => m.page),
  },
  {
    path: "/developer",
    title: "Developer",
    icon: "code",
    section: "admin",
    subsections: [
      { id: "dev-keys", label: "API Keys" },
      { id: "dev-endpoints", label: "Endpoints" },
    ],
    load: () => import("../../DOM/pages/developer").then(m => m.page),
  },
];

/** Icon SVG paths for sidebar nav items */
export const icons: Record<string, string> = {
  account: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
  workflow: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><line x1="8.5" y1="6" x2="15.5" y2="6"/><path d="M6 8.5 L12 15.5"/><path d="M18 8.5 L12 15.5"/></svg>`,
  video: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`,
  spatial: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h7v7H3z"/><path d="M14 3h7v7h-7z"/><path d="M8.5 14l-2.5 2.5L8.5 19"/><path d="M3 17h7"/><path d="M14 14h7v7h-7z" opacity="0.4"/></svg>`,
  apps: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="2"/><rect x="13" y="3" width="8" height="8" rx="2"/><rect x="3" y="13" width="8" height="8" rx="2"/><rect x="13" y="13" width="8" height="8" rx="2"/></svg>`,
  broadcast: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></svg>`,
  help: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
  glasses: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="14" r="4.5"/><circle cx="17" cy="14" r="4.5"/><path d="M11.5 14c.3-.5.7-.8 1.2-.8s.9.3 1.2.8"/><path d="M2.5 12.5l2-3.5c.3-.5.8-.5 1 0l1.5 2.5"/><path d="M21.5 12.5l-2-3.5c-.3-.5-.8-.5-1 0l-1.5 2.5"/></svg>`,
};
